import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { CrmLayout } from "@/components/layouts/CrmLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Skeleton } from "@/components/ui";
import { DashboardPage } from "@/pages/DashboardPage";
import { ContactsPage } from "@/pages/ContactsPage";
import { ContactDetailPage } from "@/pages/ContactDetailPage";
import { ListsPage } from "@/pages/ListsPage";
import { InboxPage } from "@/pages/InboxPage";
import { MailPage } from "@/pages/MailPage";
import { CampaignsPage } from "@/pages/CampaignsPage";
import { PipelinesPage } from "@/pages/PipelinesPage";
import { CalendarPage } from "@/pages/CalendarPage";
import { TasksPage } from "@/pages/TasksPage";
import { TeamPage } from "@/pages/TeamPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { PaymentsPage } from "@/pages/PaymentsPage";
import { SchedulingLinksPage } from "@/pages/SchedulingLinksPage";
import { BookingPage } from "@/pages/BookingPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { StoreProductsPage } from "@/pages/store/ProductsPage";
import { StoreCollectionsPage } from "@/pages/store/CollectionsPage";
import { StoreOrdersPage } from "@/pages/store/OrdersPage";
import { StoreCustomersPage } from "@/pages/store/CustomersPage";
import { StoreDiscountsPage } from "@/pages/store/DiscountsPage";
import { StoreSitePage } from "@/pages/store/SitePage";
import { StoreSettingsPage } from "@/pages/store/SettingsPage";
import { AffiliatesPage } from "@/pages/affiliates/AffiliatesPage";

// Lazy-load the heavy chart/graph routes so xyflow + dagre (~270 KB) and
// recharts (~110 KB) don't ship to users who never visit those pages.
// Most sessions don't touch /automations or /reports — paying for those
// libraries on every login adds up fast.
const AutomationsPage = lazy(() => import("@/pages/AutomationsPage").then((m) => ({ default: m.AutomationsPage })));
// Lazy: pulls in the WebRTC SDK, which most sessions never need.
const DialerPage      = lazy(() => import("@/pages/DialerPage"));
const ReportsPage     = lazy(() => import("@/pages/ReportsPage").then((m) => ({ default: m.ReportsPage })));
// Traffic pulls in recharts + the funnel builder; nobody pays for it until
// they open the page.
const TrafficPage     = lazy(() => import("@/pages/TrafficPage").then((m) => ({ default: m.TrafficPage })));
// Social planner (feed previews + calendar + composer for Instagram, TikTok,
// YouTube, Facebook, X) — only loaded when opened.
const SocialPage = lazy(() => import("@/pages/social/SocialPage").then((m) => ({ default: m.SocialPage })));
const SocialCalendarPage = lazy(() => import("@/pages/social/SocialCalendarPage").then((m) => ({ default: m.SocialCalendarPage })));
// Store overview renders a recharts revenue chart — same lazy treatment.
const StoreOverviewPage = lazy(() => import("@/pages/store/OverviewPage").then((m) => ({ default: m.StoreOverviewPage })));

function PageSpinner() {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <Routes>
        {/* Public booking link — no auth, no CrmLayout chrome. The slug
            scopes which calendar's availability is shown. */}
        <Route path="/book/:slug" element={<BookingPage />} />

        {/* Authenticated CRM */}
        <Route element={<CrmLayout />}>
          <Route path="/"                element={<DashboardPage />} />
          <Route path="/contacts"        element={<ContactsPage />} />
          <Route path="/contacts/:id"    element={<ContactDetailPage />} />
          <Route path="/lists"           element={<ListsPage />} />
          <Route path="/inbox"           element={<InboxPage />} />
          <Route path="/inbox/:contactId" element={<InboxPage />} />
          <Route path="/mail"            element={<MailPage />} />
          <Route path="/mail/:threadId"  element={<MailPage />} />
          <Route path="/campaigns"       element={<CampaignsPage />} />
          <Route path="/campaigns/:id"   element={<CampaignsPage />} />
          <Route path="/pipelines"       element={<PipelinesPage />} />
          <Route path="/calendar"        element={<CalendarPage />} />
          <Route path="/scheduling-links" element={<SchedulingLinksPage />} />
          <Route path="/tasks"           element={<TasksPage />} />
          <Route path="/team"            element={<TeamPage />} />
          <Route path="/team/:userId"    element={<TeamPage />} />
          <Route path="/dialer"          element={<Suspense fallback={<PageSpinner />}><DialerPage /></Suspense>} />
          <Route path="/social"             element={<Suspense fallback={<PageSpinner />}><SocialPage /></Suspense>} />
          <Route path="/social/calendar"    element={<Suspense fallback={<PageSpinner />}><SocialCalendarPage /></Suspense>} />
          {/* The first version was Instagram-only. */}
          <Route path="/instagram"          element={<Navigate to="/social" replace />} />
          <Route path="/instagram/calendar" element={<Navigate to="/social/calendar" replace />} />
          <Route path="/automations"     element={<Suspense fallback={<PageSpinner />}><AutomationsPage /></Suspense>} />
          <Route path="/automations/:id" element={<Suspense fallback={<PageSpinner />}><AutomationsPage /></Suspense>} />
          <Route path="/reports"         element={<Suspense fallback={<PageSpinner />}><ReportsPage /></Suspense>} />
          <Route path="/traffic"         element={<Suspense fallback={<PageSpinner />}><TrafficPage /></Suspense>} />
          <Route path="/payments"        element={<PaymentsPage />} />
          <Route path="/payments/:tab"   element={<PaymentsPage />} />
          <Route path="/store"             element={<Suspense fallback={<PageSpinner />}><StoreOverviewPage /></Suspense>} />
          <Route path="/store/products"    element={<StoreProductsPage />} />
          <Route path="/store/collections" element={<StoreCollectionsPage />} />
          <Route path="/store/orders"      element={<StoreOrdersPage />} />
          <Route path="/store/customers"   element={<StoreCustomersPage />} />
          <Route path="/store/discounts"   element={<StoreDiscountsPage />} />
          <Route path="/store/site"        element={<StoreSitePage />} />
          <Route path="/store/settings"    element={<StoreSettingsPage />} />
          <Route path="/affiliates"       element={<AffiliatesPage />} />
          <Route path="/settings"        element={<SettingsPage />} />
          {/* 404 catch-all — sits inside CrmLayout so the user keeps the
              sidebar nav and can recover. */}
          <Route path="*"                element={<NotFoundPage />} />
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}
